import { auth0 } from 'Common/plugins/auth0'

/* eslint-disable @typescript-eslint/no-explicit-any */
class WebSocketConnection {
  _url!: string
  onopen: ((ev: Event) => any) | null
  onclose: ((ev: CloseEvent) => any) | null
  onmessage: ((ev: MessageEvent<any>) => any) | null
  onerror: ((ev: Event) => any) | null
  maxRetries: number
  retryInterval: number
  retryCount: number
  openOnInit: boolean
  websocket?: WebSocket

  constructor(
    url: string,
    onopen: ((ev: Event) => any) | null,
    onclose: ((ev: CloseEvent) => any) | null,
    onmessage: ((ev: MessageEvent<any>) => any) | null,
    onerror: ((ev: Event) => any) | null,
    maxRetries?: number,
    retryInterval?: number,
    openOnInit?: boolean
  ) {
    this.url = url
    this.onopen = onopen
    this.onclose = onclose
    this.onmessage = onmessage
    this.onerror = onerror
    this.maxRetries = maxRetries || 3
    this.retryInterval = retryInterval || 3000
    this.retryCount = 0
    this.openOnInit = openOnInit !== undefined ? openOnInit : true
    if (this.openOnInit === true) this.create()
  }

  /**
   * Binds this onOpen function to the websocket onOpen event listener
   * @param webSocket WebSocket object
   */
  bindOnOpen(webSocket: WebSocket) {
    if (this.onopen) {
      if (typeof this.onopen !== 'function')
        throw new Error('WebSocket connection onopen property must be a function if defined.')
      webSocket.onopen = this.onopen
    }
  }

  /**
   * Binds this onClose function to the websocket onClose event listener
   * @param webSocket WebSocket object
   */
  bindOnClose(webSocket: WebSocket) {
    if (this.onclose) {
      if (typeof this.onclose !== 'function')
        throw new Error('WebSocket connection onclose property must be a function if defined.')
      webSocket.onclose = this.onclose
    }
  }

  /**
   * Binds this onMessage function to the websocket onMessage event listener
   * @param webSocket WebSocket object
   */
  bindOnMessage(webSocket: WebSocket) {
    if (this.onmessage) {
      if (typeof this.onmessage !== 'function')
        throw new Error('WebSocket connection onmessage property must be a function if defined.')
      webSocket.onmessage = this.onmessage
    }
  }

  /**
   * Binds retry logic and this onError function to the websocket onError event listener.
   * @param webSocket WebSocket object
   */
  bindOnError(webSocket: WebSocket) {
    webSocket.onerror = (e: Event) => {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++
        setTimeout(() => this.create(), this.retryInterval)
      } else if (this.onerror && typeof this.onerror === 'function') {
        this.onerror(e)
      }
    }
  }

  /**
   * Binds onOpen, onClose, onMessage, and onError to web socket event listerners
   * @param websocket WebSocket object
   */
  bindListerners(websocket: WebSocket) {
    this.bindOnOpen(websocket)
    this.bindOnClose(websocket)
    this.bindOnMessage(websocket)
    this.bindOnError(websocket)
  }

  /**
   * Creates the websocket connection with this url.
   */
  async create() {
    const token = await auth0.getAccessTokenSilently()
    const encodedToken = encodeURIComponent(token)
    const websocket = new WebSocket(`${this.url}?token=Bearer ${encodedToken}`)
    this.bindListerners(websocket)
    this.websocket = websocket
  }

  /**
   * Closes the websocket connection.
   */
  close() {
    if (
      this.websocket &&
      this.websocket.readyState !== WebSocket.CLOSED &&
      this.websocket.readyState !== WebSocket.CLOSING
    )
      this.websocket.close()
  }

  /**
   * Transmits the provided data through the websocket connection.
   * @param data Data to be transmitted through the websocket connection.
   */
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (this.websocket && this.websocket.OPEN) {
      this.websocket.send(data)
    } else {
      console.error(`WebSocket at ${this.url} is not connected`)
    }
  }

  /**
   * Takes supplied url and matches the clients current protocal and host. Sets the _url property to the computed final url.
   * @param url Full or patial url for websocket connection
   * @returns void
   */
  set url(url: string) {
    //TODO Uncomment code once jeteye-review in dev is served over https
    // const isFullUrl = url.startsWith('ws:') || url.startsWith('wss:')
    // const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    // const finalUrl = isFullUrl ? url.replace(/^wss?:/, `${protocol}:`) : `${protocol}://${window.location.host}${url}`
    // this._url = finalUrl
    this._url = url
  }

  /**
   * Returns _url property value
   */
  get url(): string {
    return this._url
  }
}

export { WebSocketConnection }
